'use client';

import { useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { AUTH_EXPIRED_EVENT_NAME } from '~/lib/auth-error';

import { useAuthModal } from './auth-modal-context';

/**
 * Listens for 'auth:session-expired' custom DOM events.
 * Clears the React Query user/wallet cache so the navbar hides "My Account"
 * and shows Sign Up/Login buttons, then opens the auth modal for re-login.
 *
 * Does NOT call supabase.auth.signOut() because that fires the SIGNED_OUT
 * onAuthStateChange event, which triggers window.location.reload() in
 * useAuthChangeListener — losing the auth modal. Instead we just clear
 * the cached user data so all `useUser()` consumers see undefined.
 *
 * Debounces so multiple simultaneous server action failures don't spam the modal.
 * Must be mounted inside <AuthModalProvider>.
 */
export function SessionExpiredWatcher() {
  const { openAuthModal, isOpen } = useAuthModal();
  const queryClient = useQueryClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handlingRef = useRef(false);

  useEffect(() => {
    const handler = () => {
      // Don't open if already open or already handling
      if (isOpen || handlingRef.current) return;

      // Debounce: ignore rapid-fire events within 100ms
      if (debounceRef.current) return;

      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
      }, 100);

      handlingRef.current = true;

      // Clear user + wallet query cache so navbar/UI reflects signed-out state.
      // setQueryData to undefined immediately makes useUser() return undefined,
      // so isAuthenticated becomes false and "My Account" hides instantly.
      queryClient.setQueryData(['supabase:user'], undefined);
      queryClient.removeQueries({ queryKey: ['wallet:balance'] });

      handlingRef.current = false;

      // Open auth modal so user can re-login
      openAuthModal('sign-in');
    };

    window.addEventListener(AUTH_EXPIRED_EVENT_NAME, handler);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT_NAME, handler);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [openAuthModal, isOpen, queryClient]);

  return null;
}
